import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { CampaignFormValues } from "../CampaignForm";
import { 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl, 
  FormDescription, 
  FormMessage 
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BeerIcon, PlusCircle, Search } from "lucide-react";
import { VenueForm } from "./VenueForm";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

type Venue = {
  id: string;
  name: string;
  address?: string | null;
  city?: string | null;
};

type LocationSelectorProps = {
  form: UseFormReturn<CampaignFormValues>;
};

export function LocationSelector({ form }: LocationSelectorProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddVenueDialog, setShowAddVenueDialog] = useState(false);
  
  // Fetch venues from Supabase
  const { data: venues = [], isLoading, error, refetch } = useQuery({
    queryKey: ['venues'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('venues')
        .select('id, name, address, city')
        .order('name');
      
      if (error) throw error;
      return data as Venue[];
    }
  });

  // Filter venues based on search query
  const filteredVenues = venues.filter(venue => 
    venue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (venue.city && venue.city.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (venue.address && venue.address.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleAddVenue = async (newVenue: any) => {
    try {
      console.log("Adding new venue:", newVenue);
      
      // Validate required fields
      if (!newVenue.name || !newVenue.address) {
        throw new Error("Name and address are required");
      }
      
      const { data, error } = await supabase
        .from('venues')
        .insert(newVenue)
        .select()
        .single();
      
      if (error) {
        console.error("Supabase error adding venue:", error);
        if (error.code === '23505') { // Unique violation
          toast.error("A venue with this name already exists");
        } else if (error.code === '23514') { // Check violation
          toast.error("Invalid venue data. Please check your input.");
        } else {
          toast.error(`Failed to add venue: ${error.message}`);
        }
        throw error;
      }
      
      console.log("Venue added successfully:", data);
      
      // Add the new venue to the selected venues
      const currentVenues = form.getValues("venues") || [];
      form.setValue("venues", [...currentVenues, data.id], { shouldValidate: true });
      
      // Close the dialog and refresh the venues list
      setShowAddVenueDialog(false);
      refetch();
      
      toast.success("Venue added successfully");
    } catch (error) {
      console.error("Error in handleAddVenue:", error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Failed to add venue. Please try again.");
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Redeem Locations</h3>
        <p className="text-sm text-muted-foreground">
          Select venues where this voucher can be redeemed
        </p>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button 
          type="button" 
          onClick={() => setShowAddVenueDialog(true)}
          className="flex items-center gap-1"
        >
          <PlusCircle className="h-4 w-4" />
          Add Venue
        </Button>
      </div>

      <FormField
        control={form.control}
        name="venues"
        render={() => (
          <FormItem>
            {isLoading ? (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 rounded-md" />
                ))}
              </div>
            ) : error ? (
              <div className="text-destructive">
                Failed to load venues. Please try again.
              </div>
            ) : filteredVenues.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-muted-foreground">No venues match your search</p>
                <Button 
                  variant="link" 
                  onClick={() => setShowAddVenueDialog(true)}
                  className="mt-2"
                >
                  Add a new venue
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {filteredVenues.map((venue) => (
                  <FormField
                    key={venue.id}
                    control={form.control}
                    name="venues"
                    render={({ field }) => {
                      return (
                        <FormItem
                          key={venue.id}
                          className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4"
                        >
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(venue.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  field.onChange([...field.value, venue.id]);
                                } else {
                                  field.onChange(
                                    field.value?.filter(
                                      (value) => value !== venue.id
                                    )
                                  );
                                }
                              }}
                            />
                          </FormControl>
                          <div>
                            <FormLabel className="cursor-pointer font-normal">
                              {venue.name}
                            </FormLabel>
                            {venue.address && (
                              <p className="text-xs text-muted-foreground">
                                {venue.address}{venue.city ? `, ${venue.city}` : ''}
                              </p>
                            )}
                          </div>
                        </FormItem>
                      );
                    }}
                  />
                ))}
              </div>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="isDraft"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Save as draft</FormLabel>
              <FormDescription>
                This campaign won't be active until published
              </FormDescription>
            </div>
          </FormItem>
        )}
      />

      <Dialog open={showAddVenueDialog} onOpenChange={setShowAddVenueDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Venue</DialogTitle>
          </DialogHeader>
          <VenueForm onSubmit={handleAddVenue} onCancel={() => setShowAddVenueDialog(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
